import { requestJson } from "@/lib/api";

export type PaymentMethod = "BOLETO" | "PIX" | "TRANSFERENCIA" | "DINHEIRO";
export type ReceivableOrigin = "MANUAL" | "PEDIDO_EXPEDIDO" | "PEDIDO_ENTREGUE" | "IMPORTACAO_HSE";
// Vocabulário atual (Etapa 3): ABERTO / PARCIAL / QUITADO / CANCELADO. Os valores antigos permanecem no
// tipo apenas por compatibilidade durante a publicação (a API pode devolvê-los em clientes antigos).
export type ReceivableStatus =
  | "ABERTO"
  | "PARCIAL"
  | "QUITADO"
  | "CANCELADO"
  | "A_VENCER"
  | "VENCIDO"
  | "RECEBIDO"
  | "CONCILIADO";

export interface ReceivableListItem {
  id: string;
  number: number;
  customerId: string;
  customerName: string;
  origin: ReceivableOrigin;
  document: string | null;
  installmentNumber: number;
  installmentCount: number;
  issueDate: string;
  dueDate: string;
  netAmount: string;
  receivedAmount: string;
  balance: string;
  paymentMethod: PaymentMethod;
  status: ReceivableStatus;
  overdue: boolean;
  orderCancelled: boolean;
}

export interface ReceivableEvent {
  id: string;
  type: string;
  title: string;
  details: string | null;
  field: string | null;
  oldValue: string | null;
  newValue: string | null;
  actorEmail: string | null;
  actorRole: string | null;
  at: string;
}

export interface ReceivablePayment {
  id: string;
  amount: string;
  paidAt: string;
  paymentMethod: PaymentMethod;
  externalReference: string | null;
  notes: string | null;
  createdByEmail: string | null;
  createdAt: string;
  reversed: boolean;
  reversedAt: string | null;
  reversedByEmail: string | null;
  reversedReason: string | null;
}

export interface ReceivableDetail extends ReceivableListItem {
  billingCustomerId: string | null;
  orderId: string | null;
  deliveryUnitId: string | null;
  nfNumber: string | null;
  originalAmount: string;
  discountAmount: string;
  interestAmount: string;
  paymentTermDays: number | null;
  notes: string | null;
  cancelledAt: string | null;
  cancelledReason: string | null;
  createdByEmail: string | null;
  updatedByEmail: string | null;
  createdAt: string;
  updatedAt: string;
  payments: ReceivablePayment[];
  events: ReceivableEvent[];
}

export interface RegisterPaymentInput {
  amount: number;
  paidAt: string;
  paymentMethod: PaymentMethod;
  externalReference?: string;
  notes?: string;
  requestId: string;
}

export interface ReceivableCreateInput {
  customerId: string;
  nfNumber?: string;
  document?: string;
  issueDate: string;
  dueDate?: string;
  paymentTermDays?: number;
  installmentNumber?: number;
  installmentCount?: number;
  originalAmount: number;
  discountAmount?: number;
  paymentMethod?: PaymentMethod;
  notes?: string;
}

export interface ReceivableUpdateInput {
  nfNumber?: string;
  document?: string;
  issueDate?: string;
  dueDate?: string;
  originalAmount?: number;
  discountAmount?: number;
  paymentMethod?: PaymentMethod;
  notes?: string;
}

const BASE = "/api/v1/finance/receivables";

export async function fetchReceivables(): Promise<ReceivableListItem[]> {
  return requestJson<ReceivableListItem[]>(BASE);
}

export async function fetchReceivable(id: string): Promise<ReceivableDetail> {
  return requestJson<ReceivableDetail>(`${BASE}/${id}`);
}

export async function createReceivable(input: ReceivableCreateInput): Promise<ReceivableDetail> {
  return requestJson<ReceivableDetail>(BASE, { method: "POST", body: input });
}

export async function updateReceivable(id: string, input: ReceivableUpdateInput): Promise<ReceivableDetail> {
  return requestJson<ReceivableDetail>(`${BASE}/${id}`, { method: "PATCH", body: input });
}

export async function cancelReceivable(id: string, reason: string): Promise<ReceivableDetail> {
  return requestJson<ReceivableDetail>(`${BASE}/${id}/cancel`, { method: "POST", body: { reason } });
}

export async function registerPayment(id: string, input: RegisterPaymentInput): Promise<ReceivableDetail> {
  return requestJson<ReceivableDetail>(`${BASE}/${id}/payments`, { method: "POST", body: input });
}

export async function reversePayment(paymentId: string, reason: string): Promise<ReceivableDetail> {
  return requestJson<ReceivableDetail>(`${BASE}/payments/${paymentId}/reverse`, {
    method: "POST",
    body: { reason },
  });
}

/** Gera uma chave de idempotência por operação de recebimento (reutilizada em reenvios da mesma). */
export function newRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export interface ReceivableGenerationFailure {
  orderId: string;
  orderNumber: number | null;
  customerName: string | null;
  expeditionDate: string | null;
  reason: string | null;
  attempts: number;
  lastAttemptAt: string | null;
}

export async function fetchGenerationFailures(): Promise<ReceivableGenerationFailure[]> {
  return requestJson<ReceivableGenerationFailure[]>(`${BASE}/generation/failures`);
}

export async function retryGeneration(orderId: string): Promise<void> {
  await requestJson<void>(`${BASE}/generation/${orderId}/retry`, { method: "POST" });
}

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  BOLETO: "Boleto",
  PIX: "PIX",
  TRANSFERENCIA: "Transferência/Depósito",
  DINHEIRO: "Dinheiro",
};

export const RECEIVABLE_STATUS_LABELS: Record<ReceivableStatus, string> = {
  ABERTO: "Aberto",
  PARCIAL: "Parcial",
  QUITADO: "Quitado",
  CANCELADO: "Cancelado",
  // Compatibilidade (valores antigos da API):
  A_VENCER: "A vencer",
  VENCIDO: "Vencido",
  RECEBIDO: "Recebido",
  CONCILIADO: "Conciliado",
};
